import React from 'react';
import { sortBy, isEmpty } from 'lodash';
import styled from 'styled-components';
import { themes } from 'kb-shared';
import moment from 'moment';
import Media from 'react-media';
import { DailyInstruction } from 'kb-shared/types';

import FullTimeline from './Timeline';
import Checklist from 'components/Dashboard/MedicationsWidget/Checklist';
import {
  InstructionText,
  InstructionTitle
} from 'components/Dashboard/MedicationsWidget/MedicationsWidget';

const { fonts, colors, breakpoints } = themes;
interface SubstepProps {
  substeps: Array<{ date?: string; component: JSX.Element }>;
  dailyInstructions?: DailyInstruction[];
}

type Substep = { date?: string; component: JSX.Element } | DailyInstruction;

const SubstepWrapper = ({ substeps, dailyInstructions }: SubstepProps) => {
  const instructionsWithSubstep = dailyInstructions?.filter(instruction => {
    const instructionDate = moment(instruction.date).utc();
    return !!substeps.find(substep => {
      const substepDate = moment(substep.date).utc();
      return substepDate.isSame(instructionDate, 'day');
    });
  });
  const instructionsWithSubstepIds = instructionsWithSubstep?.map(step => step.id) || [];
  const independentInstructions =
    dailyInstructions?.filter(
      instruction =>
        !instructionsWithSubstepIds.includes(instruction.id) && !isEmpty(instruction.drugs)
    ) || [];
  const instructionsAndSubsteps = sortBy(
    [...substeps, ...independentInstructions],
    item => item.date
  );

  const renderDate = (date: string) => {
    const utcMomentDate = moment(date).utc();
    const momentDate = moment(date);
    return (
      <DateWrapper>
        <time>{utcMomentDate.format('ddd M/D')}</time>
        <time>{utcMomentDate.hour() !== 0 && momentDate.format('h:mmA')}</time>
      </DateWrapper>
    );
  };

  return (
    <StepContent>
      <Media query={{ minWidth: breakpoints.tabletPortrait }}>
        {desktop => {
          return instructionsAndSubsteps.map((substep: any) => {
            if (!!substep?.component) {
              const instruction =
                dailyInstructions &&
                dailyInstructions.find(instruction => {
                  const instructionDate = moment(instruction.date).utc();
                  return moment(substep.date)
                    .utc()
                    .isSame(instructionDate, 'day');
                });
              const drugs = instruction?.drugs;
              const instructions = instruction?.instructions;
              return (
                <Wrapper key={substep.component.key || undefined}>
                  {desktop && substep.date ? renderDate(substep.date) : null}
                  <TimelineWrapper>
                    <FullTimeline
                      substep={true}
                      current={false}
                      completed={false}
                      expanded={false}
                    />
                  </TimelineWrapper>
                  <TodoBlock>
                    {desktop ? null : substep.date && renderDate(substep.date)}
                    {substep.component}
                    {drugs && (
                      <MedicationsWrapper>
                        <Checklist drugs={drugs} />
                        {instructions && instructions.trim() !== '' && (
                          <>
                            <InstructionTitle>Notes</InstructionTitle>
                            <InstructionText>{instructions}</InstructionText>
                          </>
                        )}
                      </MedicationsWrapper>
                    )}
                  </TodoBlock>
                </Wrapper>
              );
            } else {
              const instructions = substep.instructions;
              return (
                <Wrapper key={substep.id || undefined}>
                  {desktop && substep.date ? renderDate(substep.date) : null}
                  <TimelineWrapper>
                    <FullTimeline
                      substep={true}
                      current={false}
                      completed={false}
                      expanded={false}
                    />
                  </TimelineWrapper>
                  <TodoBlock>
                    {desktop ? null : substep.date && renderDate(substep.date)}
                    {substep.drugs && (
                      <MedicationsWrapper>
                        <Checklist drugs={substep.drugs} />
                        {instructions && instructions.trim() !== '' && (
                          <>
                            <InstructionTitle>Notes</InstructionTitle>
                            <InstructionText>{instructions}</InstructionText>
                          </>
                        )}
                      </MedicationsWrapper>
                    )}
                  </TodoBlock>
                </Wrapper>
              );
            }
          });
        }}
      </Media>
    </StepContent>
  );
};

export default SubstepWrapper;

const StepContent = styled.div`
  ${fonts.FoundersGrotesk};
  color: ${colors.KBNight};
  cursor: default;
  margin-top: 2px;
  margin-right: 40px;
  margin-bottom: 28px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    margin: 2px 96px;
    margin-bottom: 28px;
  }
`;

const DateWrapper = styled.div`
  position: static;
  ${({ theme }) => theme.fonts.FoundersGrotesk};
  display: flex;
  flex-direction: row;
  font-size: 14px;
  font-weight: bold;
  > time {
    margin: 3px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    position: absolute;
    flex-direction: column;
    ${({ theme }) => theme.fonts.DomaineDisplay};
    font-weight: normal;
    width: 120px;
    text-align: end;
    font-size: 24px;
    padding-top: 18px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  background: white;
  flex-direction: column;
  position: relative;
  margin: 2px 0;
  padding-right: 24px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
    flex-direction: row;
  }
`;

const TimelineWrapper = styled.div`
  position: absolute;
  padding-top: 30px;
  top: 0;
  bottom: 0;
  left: 16px;

  @media screen and (min-width: ${breakpoints.tabletPortrait}) {
    padding-top: 18px;
    margin-left: 108px;
  }
`;

const TodoBlock = styled.div`
  margin-left: 70px;
  padding-top: 34px;
  padding-bottom: 28px;

  @media screen and (min-width: ${breakpoints.tabletPortrait}) {
    padding-bottom: 0px;
    padding-top: 18px;
    margin-left: 173px;
    width: 100%;
  }
`;

const MedicationsWrapper = styled.div`
  background-color: ${themes.colors.pinkLight};
  padding: 20px;
  margin: 20px 0px;
}
`;
